import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import { useOnboardingAnswersOptional } from '@/services/db/onboarding-answers/queries';
import { hasAnsweredOnboarding } from './answered';
import { useIsAccountVerified } from './account-verification';
import { useEssentialsOnboardingGate } from './completion';
import type { OnboardingEntryInput } from './entry-redirect';

/** Exactly what the entry selector reads, plus the hook's own loading state. */
export type OnboardingSignals = OnboardingEntryInput & {
  /** While true every other field is provisional — hold, don't act on them. */
  isLoading: boolean;
};

/** Every input the onboarding routing decisions read, gathered once. */
export function useOnboardingSignals(): OnboardingSignals {
  const {
    isEligible: isChecklistCohort,
    isOnboardingEligible,
    isComplete,
    isLoading: isGateLoading,
  } = useEssentialsOnboardingGate();
  const isVerified = useIsAccountVerified();
  const isAiEnabled = useAccountSetting(AccountSetting.AiEnabled);
  // Cohort-gated so ineligible accounts fire no request.
  const answersQuery = useOnboardingAnswersOptional({ queryConfig: { enabled: isOnboardingEligible } });

  // A disabled query stays pending forever, hence the isEligible guard.
  const isLoading = isGateLoading || (isOnboardingEligible && answersQuery.isPending);

  return {
    isEligible: isOnboardingEligible,
    isChecklistCohort,
    isComplete,
    isVerified,
    isAiEnabled,
    hasAnswered: hasAnsweredOnboarding({ answers: answersQuery.data?.answers, isAiEnabled }),
    hasUntrustedSignal: answersQuery.isError,
    isLoading,
  };
}
